import { useAnimate } from 'framer-motion';
import { useEffect, useRef, useState, createContext, useContext } from 'react';
import dayjs from 'dayjs';
import classNames from 'classnames';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

const CountDownContext = createContext(null);

const Countdown = ({ endDate, className, ...props }) => {
  if (!endDate) return null;

  return (
    <CountDownContext.Provider value={{ endDate }}>
      <div
        className={classNames('flex items-center gap-5 md:gap-[3.75rem] lg:gap-20 mb-8 md:mb-6', className)}
        {...props}
      >
        <CountdownItem unit="Month" text="months" />

        <CountdownItem unit="Day" text="days" />

        <CountdownItem unit="Hour" text="hours" />

        <CountdownItem unit="Minute" text="minutes" />

        <CountdownItem unit="Second" text="seconds" />
      </div>
    </CountDownContext.Provider>
  );
};

export default Countdown;

const CountdownItem = ({ unit, text }) => {
  const { ref, time, hide } = useTimer(unit);

  return (
    <div
      className={classNames(
        "text-white flex-col gap-2 justify-center relative pr-5 md:pr-[3.75rem] lg:pr-20 last:pr-0 after:content-[''] after:h-[67px] after:md:h-[87px] after:w-px after:bg-white after:absolute after:right-0 last:after:hidden [&:has(+.hidden)::after]:hidden [&:has(+.hidden)]:pr-0",
        hide ? 'hidden' : 'flex',
      )}
    >
      <span
        className="font-ivy-ora text-center text-t4 -tracking-[0.02em] md:text-headingReg lg:text-title-l lg:leading-60 md:-tracking-[0.03em]"
        ref={ref}
      >
        {time.toString().padStart(2, '0')}
      </span>
      <span className="font-ivy-ora capitalize text-paragraph-l leading-6 -tracking-[0.02em] md:text-t4 lg:-tracking-[0.03em]">
        {text}
      </span>
    </div>
  );
};

const useTimer = (unit) => {
  const [ref, animate] = useAnimate();
  const { endDate } = useContext(CountDownContext);

  const intervalRef = useRef(null);
  const timeRef = useRef(0);

  const [time, setTime] = useState(0);
  const [hide, setHide] = useState(false);

  useEffect(() => {
    handleCountdown();
    intervalRef.current = setInterval(handleCountdown, 5000);

    return () => clearInterval(intervalRef.current || undefined);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate]);

  const handleCountdown = async () => {
    const diff = dayjs.duration(dayjs(endDate).diff());

    let newTime = 0;
    if (unit === 'Month') {
      newTime = diff.months();
      setHide(diff.months() <= 0);
    } else if (unit === 'Day') {
      newTime = diff.days();
    } else if (unit === 'Hour') {
      newTime = diff.hours();
    } else if (unit === 'Minute') {
      newTime = diff.minutes();
    } else if (unit === 'Second') {
      newTime = diff.seconds();
      setHide(diff.months() >= 0);
    }

    if (newTime !== timeRef.current && ref.current) {
      // Exit animation
      await animate(ref.current, { y: ['0%', '-50%'], opacity: [1, 0] }, { duration: 0.35 });

      timeRef.current = newTime;
      setTime(newTime);

      // Enter animation
      await animate(ref.current, { y: ['50%', '0%'], opacity: [0, 1] }, { duration: 0.35 });
    }
  };

  return { ref, time, hide };
};
